import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    withStyles,
} from '@material-ui/core';
import { useEffect, useRef, useState } from 'react';
import { axiosInstance, cancelToken } from '../../utils/axiosInstance';
import { fwStalledCopy as copy, endpoint, fwPairStageLabels } from '../../utils/constants';
import {
    isCancelled,
    isDisabledRefusal,
    operationKey,
    probeWorkflow,
    refusalOf,
    refusalText,
    workflowModes,
} from '../../utils/fwWorkflow';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import TitleHeader from '../TitleHeader/TitleHeader';

const styles = {
    root: {
        height: '100%',
        boxSizing: 'border-box',
        padding: '1rem 2rem',
        display: 'flex',
        flexDirection: 'column',
    },
    note: { marginBottom: '0.5rem' },
    actions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '1rem',
        marginBottom: '1rem',
    },
    table: {
        marginTop: '1rem',
        border: '1px solid #babfc7',
        borderRadius: 2,
        overflowX: 'auto',
    },
    headCell: {
        backgroundColor: '#f8f8f8',
        borderBottom: '1px solid #babfc7',
        fontWeight: 600,
        whiteSpace: 'nowrap',
    },
    cell: { borderBottom: '1px solid #dde2eb', whiteSpace: 'nowrap' },
    field: { marginTop: '1rem' },
    loadingWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        flex: 1,
    },
};

const validId = value => typeof value === 'string' && /^[1-9]\d*$/.test(value);
const candidatesOf = pair =>
    (Array.isArray(pair?.release?.candidates) ? pair.release.candidates : []).filter(
        candidate =>
            validId(candidate?.attempt_id) &&
            Number.isSafeInteger(candidate.expected_version) &&
            candidate.expected_version > 0
    );
const byteLength = value =>
    Array.from(value).reduce((size, character) => {
        const point = character.codePointAt(0);
        return size + (point <= 0x7f ? 1 : point <= 0x7ff ? 2 : point <= 0xffff ? 3 : 4);
    }, 0);
const operationText = error => {
    const refusal = refusalOf(error);
    const reason = error?.response?.data?.data?.reason ?? refusal.fieldCode;
    return copy.reasons[reason] ?? reason ?? refusalText(refusal, copy.refused);
};

const FwStalledPairs = ({ classes }) => {
    const [mode, setMode] = useState(workflowModes.LOADING);
    const [rows, setRows] = useState([]);
    const [readError, setReadError] = useState(null);
    const [outcome, setOutcome] = useState(null);
    const [pair, setPair] = useState(null);
    const [attemptId, setAttemptId] = useState('');
    const [reason, setReason] = useState('');
    const [phase, setPhase] = useState('editing');
    const [dialogError, setDialogError] = useState(null);
    const lifetime = useRef({ active: false, generation: 0 });
    const readSource = useRef(null);
    const mutationSource = useRef(null);
    const pending = useRef(null);
    const sending = useRef(false);

    const reload = async () => {
        if (sending.current) return;
        const generation = ++lifetime.current.generation;
        readSource.current?.cancel();
        readSource.current = cancelToken.source();
        const config = { cancelToken: readSource.current.token };
        const current = () => lifetime.current.active && generation === lifetime.current.generation;
        pending.current = null;
        setPhase('editing');
        setPair(null);
        setRows([]);
        setReadError(null);
        setMode(workflowModes.LOADING);
        const probe = await probeWorkflow(config);
        if (!current()) return;
        if (probe.mode !== workflowModes.CONFIGURED) {
            setMode(probe.mode);
            setReadError(refusalText(probe.refusal, copy.readError));
            return;
        }
        try {
            const response = await axiosInstance.get(`${endpoint.FW_READ_MODEL}/pairs/stalled`, {
                ...config,
                params: { limit: 200 },
            });
            if (!current()) return;
            const data = response?.data?.data;
            if (!Array.isArray(data)) throw new Error(copy.invalidRead);
            setRows(data.filter(row => validId(row?.pair_id)));
            setMode(workflowModes.CONFIGURED);
        } catch (error) {
            if (!current() || isCancelled(error)) return;
            setMode(workflowModes.ERROR);
            setReadError(refusalText(refusalOf(error), copy.readError));
        }
    };

    useEffect(() => {
        lifetime.current.active = true;
        reload();
        return () => {
            lifetime.current.active = false;
            lifetime.current.generation += 1;
            readSource.current?.cancel();
            mutationSource.current?.cancel();
        };
    }, []);

    const open = row => {
        setOutcome(null);
        pending.current = null;
        setPair(row);
        setAttemptId('');
        setReason('');
        setPhase('editing');
        setDialogError(null);
    };
    const candidate = candidatesOf(pair).find(item => item.attempt_id === attemptId);
    const valid = candidate && reason.trim() !== '' && byteLength(reason) <= 512;

    const send = async () => {
        if (sending.current || !lifetime.current.active) return;
        if (!pending.current) {
            if (!valid || !validId(pair?.pair_id) || phase !== 'editing') return;
            pending.current = Object.freeze({
                pairId: pair.pair_id,
                attemptId,
                expectedVersion: candidate.expected_version,
                reason,
                key: operationKey(),
            });
        } else if (phase !== 'uncertain') return;
        sending.current = true;
        setPhase('sending');
        setDialogError(null);
        const operation = pending.current;
        const generation = lifetime.current.generation;
        const current = () => lifetime.current.active && generation === lifetime.current.generation;
        mutationSource.current = cancelToken.source();
        try {
            const response = await axiosInstance.post(
                `${endpoint.FW_PAIRS}/${operation.pairId}/release`,
                {
                    attempt_id: operation.attemptId,
                    expected_version: operation.expectedVersion,
                    reason: operation.reason,
                },
                { headers: { 'Idempotency-Key': operation.key }, cancelToken: mutationSource.current.token }
            );
            if (!current()) return;
            const result = response?.data;
            const released =
                result?.code === 'FW_OPERATION_RECORDED' && result?.data?.reason === 'TRIAL_RELEASED';
            if (released || result?.code === 'FW_ADMISSION_DEFERRED') {
                setOutcome(
                    released
                        ? copy.success
                        : (copy.reasons[result?.data?.reason] ?? result?.data?.reason ?? copy.refused)
                );
                sending.current = false;
                await reload();
            } else {
                setPhase('blocked');
                setDialogError(copy.unknownResult);
                setRows([]);
            }
        } catch (error) {
            if (!current() || isCancelled(error)) return;
            const refusal = refusalOf(error);
            const message = operationText(error);
            if (isDisabledRefusal(refusal)) {
                setPhase('editing');
                pending.current = null;
                setPair(null);
                setRows([]);
                setMode(workflowModes.DISABLED);
                setReadError(message);
            } else if (refusal.status === null || refusal.status >= 500) {
                setPhase('uncertain');
                setDialogError(`${message} ${copy.uncertain}`);
            } else {
                setPhase('blocked');
                setDialogError(`${message} ${copy.refused}`);
                setRows([]);
            }
        } finally {
            sending.current = false;
        }
    };

    return (
        <div className={classes.root}>
            <TitleHeader title={`Moduł FW — ${copy.title}`} />
            <Typography className={classes.note}>{copy.explanation}</Typography>
            <Typography className={classes.note}>{copy.bounds}</Typography>
            <div className={classes.actions}>
                <Button
                    variant="outlined"
                    onClick={reload}
                    disabled={phase === 'sending' || mode === workflowModes.LOADING}>
                    {copy.reload}
                </Button>
            </div>
            {outcome && (
                <Typography role="status" className={classes.note}>
                    {outcome}
                </Typography>
            )}
            {readError && (
                <Typography color="error" role="alert" className={classes.note}>
                    {readError}
                </Typography>
            )}
            {mode === workflowModes.LOADING && (
                <div className={classes.loadingWrapper}>
                    <LoadingSpinner />
                </div>
            )}
            {mode === workflowModes.CONFIGURED && rows.length === 0 && (
                <Typography className={classes.note}>{copy.empty}</Typography>
            )}
            {mode === workflowModes.CONFIGURED && rows.length > 0 && (
                <div className={classes.table}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {[
                                    copy.pair,
                                    copy.stage,
                                    copy.epoch,
                                    copy.confirmed,
                                    copy.outstanding,
                                    copy.threshold,
                                    copy.revision,
                                    copy.age,
                                    copy.release,
                                ].map(label => (
                                    <TableCell key={label} className={classes.headCell}>
                                        {label}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map(row => (
                                <TableRow key={row.pair_id}>
                                    <TableCell className={classes.cell}>{row.pair_id}</TableCell>
                                    <TableCell className={classes.cell}>
                                        {fwPairStageLabels[row.stage] ?? row.stage}
                                    </TableCell>
                                    <TableCell className={classes.cell}>{row.stage_epoch}</TableCell>
                                    <TableCell className={classes.cell}>{row.confirmed}</TableCell>
                                    <TableCell className={classes.cell}>{row.outstanding}</TableCell>
                                    <TableCell className={classes.cell}>{row.threshold}</TableCell>
                                    <TableCell className={classes.cell}>{row.threshold_revision}</TableCell>
                                    <TableCell className={classes.cell}>
                                        {row.outstanding_age_seconds === null
                                            ? copy.unknownAge
                                            : `${row.outstanding_age_seconds} ${copy.seconds}`}
                                    </TableCell>
                                    <TableCell className={classes.cell}>
                                        {candidatesOf(row).length > 0 ? (
                                            <Button
                                                size="small"
                                                onClick={() => open(row)}
                                                disabled={Boolean(pair)}>
                                                {copy.release}
                                            </Button>
                                        ) : (
                                            copy.unavailable
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
            <Dialog
                open={Boolean(pair)}
                aria-labelledby="fw-release-title"
                onClose={() => {
                    if (phase === 'editing') setPair(null);
                }}
                disableEscapeKeyDown={phase !== 'editing'}
                fullWidth>
                <DialogTitle id="fw-release-title">{copy.confirm}</DialogTitle>
                <DialogContent>
                    <Typography>{`${copy.pair}: ${pair?.pair_id ?? ''}; ${copy.epoch}: ${pair?.stage_epoch ?? ''}`}</Typography>
                    <TextField
                        id="fw-release-attempt"
                        select
                        SelectProps={{ native: true }}
                        label={copy.candidate}
                        value={attemptId}
                        onChange={event => setAttemptId(event.target.value)}
                        disabled={phase !== 'editing'}
                        fullWidth
                        className={classes.field}>
                        <option value="">{copy.choose}</option>
                        {candidatesOf(pair).map(item => (
                            <option
                                key={item.attempt_id}
                                value={
                                    item.attempt_id
                                }>{`${item.attempt_id} (${copy.version}: ${item.expected_version})`}</option>
                        ))}
                    </TextField>
                    <TextField
                        id="fw-release-reason"
                        label={copy.reason}
                        value={reason}
                        onChange={event => setReason(event.target.value)}
                        disabled={phase !== 'editing'}
                        helperText={copy.reasonHint}
                        fullWidth
                        multiline
                        className={classes.field}
                    />
                    {dialogError && <Typography role="alert">{dialogError}</Typography>}
                </DialogContent>
                <DialogActions>
                    {phase === 'editing' && <Button onClick={() => setPair(null)}>{copy.cancel}</Button>}
                    {(phase === 'editing' || phase === 'sending') && (
                        <Button onClick={send} disabled={!valid || phase === 'sending'}>
                            {copy.confirm}
                        </Button>
                    )}
                    {phase === 'uncertain' && <Button onClick={send}>{copy.retry}</Button>}
                    {(phase === 'blocked' || phase === 'uncertain') && (
                        <Button onClick={reload}>{copy.reload}</Button>
                    )}
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default withStyles(styles)(FwStalledPairs);
